import { Reducer } from 'react';

import { isEmpty } from 'lodash';
import { BundlePricingSubAttribute } from 'src/constants';
import { IBundlePricingRowData, IBundlePricingValidationAction } from 'src/types/IBundlePricing';
import { areEqualSets } from 'src/util/setUtil';
import {
    isStartDateBeforeEndDate,
    isValidCurrency,
    isValidNullableDateString,
    isValidPaperPlanName,
    isValidTerritory,
    validateDateString,
    validateNumeric,
    validateRequired
} from 'src/util/validationsUtil';

import { generateGridErrorId } from '../AddPrimeAMFRateCard/rateCardValidationReducer';

export const DEFAULT_BUNDLE_PRICING_ERRORS = new Set<string>();

const BUNDLE_PRICING_COLUMN_FIELDS = [
    'territory',
    'planName',
    'planSubAttribute',
    'currency',
    'price',
    'startDate',
    'endDate'
];

/**
 * Entry point for validation reducer
 *
 * @param prevErrors Previous error set
 * @param action Reducer action
 * @returns New error set if different from previous error set, otherwise it returns previous error set
 */
export const bundlePricingValidationReducer: Reducer<Set<string>, IBundlePricingValidationAction> = (
    prevErrors,
    action
): Set<string> => {
    const newErrors = new Set<string>();

    validateRows(newErrors, action);
    validateDuplicates(newErrors, action);

    // Avoid creating new set if no new errors
    return areEqualSets(prevErrors, newErrors) ? prevErrors : newErrors;
};

/**
 * Validates cells by row
 *
 * @param errors New error set
 * @param action Reducer action
 */
const validateRows = (errors: Set<string>, action: IBundlePricingValidationAction): void => {
    const territories = new Set(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        action.territories.filter((t) => t.territoryName !== 'Global').map((t) => t.territoryCode!)
    );
    const planNamesSet = new Set(action.paperPlanNames);
    const currenciesSet = new Set(action.currencies);

    // For a specific plan/territory, there should only be one currency
    const planTerritoryCurrencyMap = new Map<string, string>();

    action.rowData.forEach((row, index) => {
        BUNDLE_PRICING_COLUMN_FIELDS.forEach((col) => {
            const cellValue = row[col as keyof IBundlePricingRowData] as string | undefined;
            const errorId = generateGridErrorId(index, col);

            switch (col) {
                case 'territory':
                    if (!validateRequired(cellValue) || !isValidTerritory(cellValue as string, territories)) {
                        errors.add(errorId);
                    }
                    break;

                case 'planName':
                    if (!isValidPaperPlanName(cellValue, planNamesSet)) {
                        errors.add(errorId);
                    }
                    break;

                case 'planSubAttribute':
                    if (!Object.values(BundlePricingSubAttribute).some((a) => a === cellValue)) {
                        errors.add(errorId);
                    }
                    break;

                case 'currency':
                    if (!isValidCurrency(cellValue, currenciesSet)) {
                        errors.add(errorId);
                    }

                    // Check if currency is different for a specific plan/territory
                    else {
                        const plan = row['planName'];
                        const territory = row['territory'];
                        if ([plan, territory].every((x) => !isEmpty(x))) {
                            const planTerritoryKey = [plan, territory].join('#');
                            if (!planTerritoryCurrencyMap.has(planTerritoryKey)) {
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                planTerritoryCurrencyMap.set(planTerritoryKey, cellValue!);
                            } else if (planTerritoryCurrencyMap.get(planTerritoryKey) !== cellValue) {
                                errors.add(errorId);
                            }
                        }
                    }

                    break;

                case 'price':
                    if (!validateRequired(cellValue) || !validateNumeric(cellValue)) {
                        errors.add(errorId);
                    }
                    break;

                case 'startDate':
                    if (!validateDateString(cellValue)) {
                        errors.add(errorId);
                    }
                    break;

                case 'endDate':
                    if (!isValidNullableDateString(cellValue)) {
                        errors.add(errorId);
                    }

                    // Start date before end date. Both must be present
                    else {
                        const startDate = row['startDate'] as string | undefined;
                        if (
                            validateDateString(startDate) &&
                            validateDateString(cellValue) &&
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            !isStartDateBeforeEndDate(startDate!, cellValue!)
                        ) {
                            errors.add(errorId);
                        }
                    }
            }
        });
    });
};

/**
 * Validates that only 1 row exists for any given territory/plan/sub-attribute/start date
 *
 * @param errors New error set
 * @param action Reducer action
 */
const validateDuplicates = (errors: Set<string>, action: IBundlePricingValidationAction): void => {
    const territoryPlanSubAttributeStartDateSet = new Set<string>();
    const territoryPlanStartDateToEndDateMap = new Map<string, string>();

    const addEntireRowToErrorSet = (index: number): void => {
        BUNDLE_PRICING_COLUMN_FIELDS.forEach((col) => {
            const errorId = generateGridErrorId(index, col);
            errors.add(errorId);
        });
    };

    action.rowData
        .filter((row) => {
            // Only process rows that are complete
            const territory = row['territory'];
            const planName = row['planName'];
            const planSubAttribute = row['planSubAttribute'];
            const startDate = row['startDate'];
            const arr = [territory, planName, planSubAttribute, startDate];
            return arr.every((x) => !isEmpty(x));
        })
        .forEach((row, index) => {
            const territory = row['territory'];
            const planName = row['planName'];
            const planSubAttribute = row['planSubAttribute'];
            const startDate = Date.parse(row['startDate']).toString();
            const arr = [territory, planName, planSubAttribute, startDate];

            if (territoryPlanSubAttributeStartDateSet.has(arr.join('#'))) {
                addEntireRowToErrorSet(index);
            } else {
                territoryPlanSubAttributeStartDateSet.add(arr.join('#'));
            }

            // All territory/plan/start date rows should have the same end date
            const endDate = row['endDate'];
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const endDateStr = isEmpty(endDate) ? '' : Date.parse(endDate!).toString();

            const territoryPlanStartDateToEndDateKey = [territory, planName, startDate].join('#');
            if (
                territoryPlanStartDateToEndDateMap.has(territoryPlanStartDateToEndDateKey) &&
                territoryPlanStartDateToEndDateMap.get(territoryPlanStartDateToEndDateKey) !== endDateStr
            ) {
                addEntireRowToErrorSet(index);
            } else {
                territoryPlanStartDateToEndDateMap.set(territoryPlanStartDateToEndDateKey, endDateStr);
            }
        });
};
